import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { AppIconsType } from '../icon/icons';
import { AppColorKeys } from '@fc-core/constants/colors';
import { IconComponent } from '../icon/icon.component';

export type NgTagColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success'
  | 'keppel';
export type NgTagSize = 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large';
export type NgTagHierarchy = 'primary' | 'secondary';

@Component({
  selector: 'fc-ng-tag',
  template: `
    <div [class]="tagClasses()">
      @if (icon()) {
        <fc-icon [icon]="icon()" [size]="iconSize()" [color]="iconColor()" />
      }
      <ng-content></ng-content>
      @if (value()) {
        <span class="truncate" [class]="textSize()">{{ value() }}</span>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: {
    class: 'flex flex-row',
  },
})
export class NgTagComponent {
  value = input<string>('');
  icon = input<AppIconsType | null>(null);
  color = input<NgTagColor>('secondary');
  size = input<NgTagSize>('md');
  rounded = input<boolean>(false);
  outlined = input<boolean>(false);
  hierarchy = input<NgTagHierarchy>('secondary');

  private normalizedSize = computed<'sm' | 'md' | 'lg'>(() => {
    switch (this.size()) {
      case 'sm':
      case 'small':
        return 'sm';
      case 'lg':
      case 'large':
        return 'lg';
      default:
        return 'md';
    }
  });

  tagClasses = computed(() => {
    const base = 'inline-flex max-w-full items-center gap-4 whitespace-nowrap';
    const sizeClass = this.sizeClass();
    const shapeClass = this.rounded() ? 'rounded-full' : 'rounded-md';
    const colorClass = this.colorClass();
    return `${base} ${sizeClass} ${shapeClass} ${colorClass}`;
  });

  iconSize = computed(() => {
    switch (this.normalizedSize()) {
      case 'lg':
        return 20;
      default:
        return 16;
    }
  });

  textSize = computed(() => {
    switch (this.normalizedSize()) {
      case 'sm':
        return 'text-xs';
      case 'lg':
        return 'text-base';
      default:
        return 'text-sm';
    }
  });

  iconColor = computed<AppColorKeys>(() => {
    if (!this.outlined() && this.hierarchy() === 'primary') {
      return 'white';
    }
    switch (this.color()) {
      case 'primary':
        return 'primary500';
      case 'secondary':
        return 'secondary400';
      case 'error':
        return 'error500';
      case 'warning':
        return 'warning500';
      case 'success':
        return 'success500';
      case 'keppel':
        return 'kepple500';
    }
  });

  private sizeClass(): string {
    switch (this.normalizedSize()) {
      case 'sm':
        return 'h-24 px-8 text-xs leading-none';
      case 'lg':
        return 'h-32 px-12 text-base leading-none';
      default:
        return 'h-28 px-10 text-sm leading-none';
    }
  }

  private colorClass(): string {
    if (this.outlined()) {
      switch (this.color()) {
        case 'primary':
          return 'border border-primary-200 text-primary-600 bg-transparent';
        case 'secondary':
          return 'border border-secondary-200 text-secondary-500 bg-transparent';
        case 'error':
          return 'border border-error-200 text-error-600 bg-transparent';
        case 'warning':
          return 'border border-warning-200 text-warning-600 bg-transparent';
        case 'success':
          return 'border border-success-200 text-success-600 bg-transparent';
        case 'keppel':
          return 'border border-keppel-200 text-keppel-600 bg-transparent';
      }
    }

    if (this.hierarchy() === 'primary') {
      switch (this.color()) {
        case 'primary':
          return 'border border-transparent bg-primary-500 text-white';
        case 'secondary':
          return 'border border-transparent bg-secondary-500 text-white';
        case 'error':
          return 'border border-transparent bg-error-500 text-white';
        case 'warning':
          return 'border border-transparent bg-warning-500 text-white';
        case 'success':
          return 'border border-transparent bg-success-500 text-white';
        case 'keppel':
          return 'border border-transparent bg-keppel-500 text-white';
      }
    }

    switch (this.color()) {
      case 'primary':
        return 'border border-transparent bg-primary-100 text-primary-600';
      case 'secondary':
        return 'border border-transparent bg-secondary-100 text-secondary-500';
      case 'error':
        return 'border border-transparent bg-error-100 text-error-600';
      case 'warning':
        return 'border border-transparent bg-warning-100 text-warning-600';
      case 'success':
        return 'border border-transparent bg-success-100 text-success-600';
      case 'keppel':
        return 'border border-transparent bg-keppel-100 text-keppel-600';
    }
  }
}
